import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import PageWrapper from '../components/PageWrapper';
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { useActiveSession } from '../ActiveSessionContext';
import { normalizeKey, normalizeType } from '../utils/normalize';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import myIcon from '../assets/logo512.png'; // adjust path
import dexie from '../offlineDB';
import { flushPendingWrites } from '../utils/flushPendingWrites'; // ✅ Make sure this is imported

const SessionViewer = () => {
  const [tunes, setTunes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionKeys, setSelectedSessionKeys] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const { user } = useAuth();
  const { inputStyle, textStyle } = useUI();
  const { activeSession, setActiveSession } = useActiveSession();
  const navigate = useNavigate();
const [collapsed, setCollapsed] = useState(false);


 useEffect(() => {
  if (!user) return;

  const preload = async () => {
    try {
      if (navigator.onLine) {
        await flushPendingWrites(user); // ✅ flush any pending writes first
      }
    } catch (err) {
      console.warn("Pending write flush failed:", err);
    }

    const sessionsRef = collection(db, 'users', user.uid, 'sessions');
    const tunesRef = collection(db, 'users', user.uid, 'tunes');

    if (navigator.onLine) {
      const unsubscribeSessions = onSnapshot(sessionsRef, async (sessionSnap) => {
        const sessionDocs = sessionSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const tuneUnsub = onSnapshot(tunesRef, (tuneSnapshot) => {
          const allTunes = tuneSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // 🧠 Save both to local Dexie
          dexie.tunes.bulkPut(allTunes).catch(() => {});
          dexie.sessions.bulkPut(sessionDocs).catch(() => {});

          const sessionsWithTunes = sessionDocs.map(session => {
            const tunesInSession = allTunes.filter(tune =>
              Array.isArray(tune.sessions) &&
              tune.sessions.some(s => s.sessionId === session.id)
            );

            return {
              id: session.id,
              location: session.location,
              startedAt: session.startedAt?.toDate?.() || new Date(),
              endedAt: session.endedAt instanceof Date
                ? session.endedAt
                : session.endedAt?.toDate?.() || (session.endedAt ? new Date(session.endedAt) : null),
              tunes: tunesInSession
            };
          });

          const sorted = sessionsWithTunes.sort((a, b) => b.startedAt - a.startedAt);
          setTunes(allTunes);
          setSessions(sorted);
        });

        return () => tuneUnsub();
      });

      return () => unsubscribeSessions();
    } else {
      // Offline fallback
      Promise.all([
        dexie.tunes.toArray(),
        dexie.sessions.toArray()
      ]).then(([allTunes, sessionDocs]) => {
        const sessionsWithTunes = sessionDocs.map(session => {
          const tunesInSession = allTunes.filter(tune =>
            Array.isArray(tune.sessions) &&
            tune.sessions.some(s => s.sessionId === session.id)
          );

          return {
            id: session.id,
            location: session.location,
            startedAt: session.startedAt || new Date(),
            endedAt: session.endedAt || null,
            tunes: tunesInSession
          };
        });

        const sorted = sessionsWithTunes.sort((a, b) => b.startedAt - a.startedAt);
        setTunes(allTunes);
        setSessions(sorted);
      });
    }
  };

  preload();
}, [user]);



  const toggleSessionSelection = (key) => {
    setSelectedSessionKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSessionKeys.length === sessions.length) {
      setSelectedSessionKeys([]);
    } else {
      setSelectedSessionKeys(sessions.map(s => s.id))
    }
  };
const deleteSelectedSessions = async () => {
  if (!user || selectedSessionKeys.length === 0) return;
  const confirmDelete = window.confirm(`Delete ${selectedSessionKeys.length} session(s)?`);
  if (!confirmDelete) return;

  const affectedTuneSnapshots = [];

  // 🔥 Delete each session
  for (const sessionId of selectedSessionKeys) {
    if (navigator.onLine) {
      await deleteDoc(doc(db, 'users', user.uid, 'sessions', sessionId));
    } else {
      await dexie.sessions.delete(sessionId);
      await dexie.pendingWrites.put({
        id: `${sessionId}__delete`,
        type: 'delete',
        collection: 'sessions',
        docId: sessionId,
        data: {}
      });
    }
  }

  // 🎯 Clean up all affected tunes
  for (const tune of tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => {
      const startedAtMillis = s.startedAt && typeof s.startedAt.toMillis === 'function'
        ? s.startedAt.toMillis()
        : s.timestamp && typeof s.timestamp.toMillis === 'function'
        ? s.timestamp.toMillis()
        : s.startedAt || s.timestamp || 0;

      return !selectedSessionKeys.includes(s.sessionId);
    });

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });

      await dexie.tunes.update(tune.id, { sessions: updatedSessions });

      await dexie.pendingWrites.put({
        id: `${tune.id}__sessionRemove`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: { sessions: updatedSessions }
      });

      if (navigator.onLine) {
        await updateDoc(ref, { sessions: updatedSessions });
      }
    }
  }

  // 🧠 Undo support
  setPendingDelete({
    tuneBackups: affectedTuneSnapshots,
    sessionKeys: selectedSessionKeys,
  });

  setSelectedSessionKeys([]);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;
  clearTimeout(undoTimer);

  for (const { id, data } of pendingDelete.tuneBackups) {
    const ref = doc(db, 'users', user.uid, 'tunes', id);

    if (navigator.onLine) {
      await setDoc(ref, data);
    }

    await dexie.tunes.put({ ...data, id }); // ensure id is preserved

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'tunes',
      docId: id,
      data
    });
  }

  // (Optional: restore deleted sessions here if needed)

  setPendingDelete(null);
  setUndoTimer(null);
};



const deleteSession = async () => {
  if (!selectedSession || !user) return;

  const confirmDelete = window.confirm(`Delete session at ${selectedSession.location}?`);
  if (!confirmDelete) return;

  const affectedTuneSnapshots = [];

  // Step 1: Delete the session itself
  if (navigator.onLine) {
    await deleteDoc(doc(db, 'users', user.uid, 'sessions', selectedSession.id));
  } else {
    await dexie.sessions.delete(selectedSession.id);
    await dexie.pendingWrites.put({
      id: `${selectedSession.id}__delete`,
      type: 'delete',
      collection: 'sessions',
      docId: selectedSession.id,
      data: {}
    });
  }

  // Step 2: Remove this session from all relevant tunes
  for (const tune of selectedSession.tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => {
      const sStartedAtMillis = s.startedAt && typeof s.startedAt.toMillis === 'function'
        ? s.startedAt.toMillis()
        : s.timestamp && typeof s.timestamp.toMillis === 'function'
        ? s.timestamp.toMillis()
        : s.startedAt || s.timestamp || 0;

      const selStartedAtMillis = selectedSession.startedAt && typeof selectedSession.startedAt.getTime === 'function'
        ? selectedSession.startedAt.getTime()
        : selectedSession.timestamp && typeof selectedSession.timestamp.toMillis === 'function'
        ? selectedSession.timestamp.toMillis()
        : selectedSession.startedAt || selectedSession.timestamp || 0;

      return sStartedAtMillis !== selStartedAtMillis;
    });

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });

      const cleanedData = updatedSessions.length > 0
        ? { sessions: updatedSessions }
        : { sessions: [] };

      await dexie.tunes.update(tune.id, cleanedData);

      await dexie.pendingWrites.put({
        id: `${tune.id}__sessionRemove`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: cleanedData
      });

      if (navigator.onLine) {
        await updateDoc(ref, cleanedData);
      }
    }
  }

  setPendingDelete({
    tuneBackups: affectedTuneSnapshots,
    sessionKeys: [selectedSession.location + '__' + (selectedSession.timestamp || selectedSession.startedAt)],
  });

  setSelectedSession(null);
  setActiveSession(null);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};


  if (selectedSession) {
    return (
      <PageWrapper>
        <button className="big-button" onClick={() => {
          setSelectedSession(null);
          setActiveSession(null);
        }}>← Back to Sessions</button>
        <h2>{selectedSession.location}</h2>
        <p style={textStyle}>{selectedSession.startedAt instanceof Date ? selectedSession.startedAt.toLocaleString() : new Date(selectedSession.startedAt || selectedSession.timestamp || 0).toLocaleString()}</p>

        <div style={{ marginBottom: '20px' }}>
<button className="big-button" onClick={() => navigate(`/editsession/${selectedSession.id}`)}>
  Edit Session
</button>

          <button className="big-button" onClick={deleteSession}>Delete Session</button>
        </div>

        <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "10px", marginBottom: "2rem" }}>
          <ul className="card-list">
            {selectedSession.tunes.map(tune => (
              <li
                key={tune.id}
                className="card"
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '10px'
                }}
                onClick={() => {
                  const sessionTunes = selectedSession.tunes.map(t => ({ ...t, id: String(t.id) }));
                  const currentIndex = sessionTunes.findIndex(t => String(t.id) === String(tune.id));

                  navigate(`/tune/${tune.id}`, {
                    state: {
                      tune,
                      sessionTunes,
                      sessionTitle: selectedSession.location,
                      currentIndex
                    }
                  });
                }}


              >
<strong>{tune.title}</strong> — {tune.genre} / {tune.type} / {normalizeKey(tune.key)}{tune.status ? ` (${tune.status})` : ''}
              </li>
            ))}
          </ul>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>




<BorderedBox
  style={{ marginBottom: '0.7rem' }}
  collapsible
  defaultVisible
  exposeCollapsed={setCollapsed}
>

  <div className="app-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>AllMySessions</span>
  </div>

  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '10px' }}>
    <button className="big-button" onClick={toggleSelectAll}>
      {selectedSessionKeys.length === sessions.length ? 'Deselect All' : 'Select All'}
    </button>
    <button className="big-button" onClick={deleteSelectedSessions} disabled={selectedSessionKeys.length === 0}>
      Delete Selected ({selectedSessionKeys.length})
    </button>
  </div>
</BorderedBox>

<div
  style={{
    overflowY: 'auto',
    boxSizing: 'border-box',
    height: `calc(100vh - ${collapsed ? 180 : 250}px)`,
    paddingRight: '0px',
    paddingLeft: '0px',
    marginBottom: '2rem',
  }}
>

  {sessions.length === 0 ? (
    <p>No sessions recorded yet.</p>
  ) : (
    <ul className="card-list">
      {sessions.map((s) => (
        <TuneCardBox
          key={s.id}
          onClick={() => {
            setSelectedSession(s);
            setActiveSession(null);
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '1 1 200px',
                minWidth: '160px'
              }}
            >
              <StyledCheckbox
                checked={selectedSessionKeys.includes(s.id)}
                onChange={() => toggleSessionSelection(s.id)}
                onClick={(e) => e.stopPropagation()}
              />

              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '0.5ch'
                  }}
                >
                  <strong style={{ fontSize: '1rem' }}>{s.location || s.startedAt.toLocaleString()}</strong>
                  <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    — {s.tunes.length} tune{s.tunes.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)',
                    marginTop: '2px'
                  }}
                >
        {(() => {
  const start = s.startedAt instanceof Date
    ? s.startedAt
    : new Date(s.startedAt || s.timestamp || 0);

  const formatted = start.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });

if (!s.endedAt) return `${formatted} — (ongoing)`;

  const end = s.endedAt instanceof Date
    ? s.endedAt
    : new Date(s.endedAt);

  const durationMs = end - start;
  const totalMinutes = Math.max(1, Math.floor(durationMs / 60000)); // ensure ≥ 1 min
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const durationLabel = [
    hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : null,
    minutes > 0 ? `${minutes} min` : null
  ].filter(Boolean).join(', ');

  return `${formatted} — ${durationLabel}`;
})()}



                </div>
              </div>
            </div>
          </div>
        </TuneCardBox>
      ))}
    </ul>
  )}
</div>


    </PageWrapper>
  );
};

export default SessionViewer;
